// The `silhouette` structure symbol set: card 05 of mockup round 054, "SOLID
// SILHOUETTES" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// Filled solid shapes on one short plinth line, the way a skyline reads: a
// mast, a chimney with smoke, three kelp blades, a tube on a dome, a crowned
// seat, a cradle with a hull, a door with the keyhole cut out, a hall with
// columns, and a coral fan. A neutral silhouette is the same shape at a
// lower alpha.
//
// The contested tell is a crack that runs through the silhouette in the
// attacker's ink, with the attacker's ink filling the piece that broke off.
// Card 05 blinks the crack with the beacon clock; the atlas bakes one still
// cell, so the crack draws at the middle of that blink (R1104).
//
// **The broken piece is a clip, and there is no clip.** Every kind is
// therefore a LIST OF POLYGONS here, and not a list of paint calls: a bar
// becomes its capsule, a disc becomes its polygon and a curve becomes its
// point list. The broken piece is then each polygon moved by the card's
// offset and cut to the region right of the crack. The crack is one polyline
// whose depth rises through every segment, so that region is the union of
// four convex bands, one per segment, and a convex band cuts a polygon with
// three half-planes.
//
// Every offset, weight, radius and alpha below is the figure card 05 draws.

import type { MarkGraphics } from '../marks';
import {
  quadratic, regular, rot, stroke, TILT, WATER,
  setOf, type StructureDraw, type StructureInk, type StructureKind, type StructureSet,
} from './shared';

/** The plinth every kind stands on. */
const PLINTH = 1.6;

/** The crack of the contested tell, from the head of the mark to its foot. */
const CRACK: readonly (readonly [number, number])[] = [
  [5, -23], [1, -11], [6, -3], [2, 6], [7, 16],
];

/** The crack as the flat point list the stroke takes. */
const CRACK_PATH: number[] = CRACK.flatMap(([x, y]) => [x, y]);

/** How far the broken piece slides away from the mark. */
const CRACK_DX = 1.5;
const CRACK_DY = 0.8;

/** The alpha the broken piece takes. */
const BROKEN_ALPHA = 0.95;

/**
 * The alpha of the crack line.
 *
 * Card 05 blinks it between 0.4 and 1 on the beacon clock. The still cell
 * takes the middle of that span (R1104).
 */
const CRACK_ALPHA = 0.7;

/** How far two bands of the crack region overlap, so no seam opens. */
const BAND_OVERLAP = 0.3;

/** One half-plane: the side where `nx·x + ny·y + d` is not negative. */
interface Half { nx: number; ny: number; d: number }

/** One convex band of the region right of the crack: three half-planes. */
type Band = readonly [Half, Half, Half];

/** The four bands, one per segment of the crack. */
const CRACK_BANDS: readonly Band[] = CRACK.slice(1).map((b, i) => {
  const [ax, ay] = CRACK[i], [bx, by] = b;
  const dx = bx - ax, dy = by - ay;
  return [
    { nx: 0, ny: 1, d: -(ay - BAND_OVERLAP) },
    { nx: 0, ny: -1, d: by + BAND_OVERLAP },
    { nx: dy, ny: -dx, d: -(dy * ax - dx * ay) },
  ] as const;
});

/** One shape of one silhouette. `water` knocks it out of the shape under it. */
interface Solid { pts: number[]; water?: boolean }

/** A stroked bar with round ends, as the polygon of its capsule. */
function capsule(
  x0: number, y0: number, x1: number, y1: number, w: number,
): number[] {
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  const a0 = Math.atan2((y1 - y0) / len, (x1 - x0) / len);
  const r = w / 2, steps = 8, pts: number[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = a0 - Math.PI / 2 + (Math.PI * i) / steps;
    pts.push(x1 + Math.cos(a) * r, y1 + Math.sin(a) * r);
  }
  for (let i = 0; i <= steps; i++) {
    const a = a0 + Math.PI / 2 + (Math.PI * i) / steps;
    pts.push(x0 + Math.cos(a) * r, y0 + Math.sin(a) * r);
  }
  return pts;
}

/** A filled disc, as the polygon of its circle. */
function round(x: number, y: number, r: number): number[] {
  return regular(x, y, r, 20, 0);
}

/** An arc, appended to a point list the caller opened. */
function arcInto(
  out: number[], cx: number, cy: number, r: number, a0: number, a1: number,
): void {
  const steps = 16;
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    out.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
}

/**
 * The silhouette of one kind, as the list of the shapes it fills.
 *
 * It is `sil()` of card 05. The order is the card's, because a shape in the
 * water's dark knocks out the shape drawn before it.
 */
function solidsOf(kind: StructureKind): Solid[] {
  const out: Solid[] = [];
  const add = (pts: number[], water?: boolean): void => {
    out.push(water ? { pts, water } : { pts });
  };
  switch (kind) {
    case 'spire':
      // The mast, its head and its yard.
      add([-5, 14, 5, 14, 0.8, -12, -0.8, -12]);
      add(capsule(0, -12, 0, -17, 1.6));
      add(round(0, -18, 2.4));
      add(capsule(-5, -15, 5, -15, 1.4));
      break;
    case 'forge':
      // The chimney, with three balls of smoke over its mouth.
      add([-11, 14, -8, 8, -5, -3, 5, -3, 8, 8, 11, 14]);
      add(round(1, -8, 2.6));
      add(round(4, -13, 2.1));
      add(round(1, -17, 1.6));
      break;
    case 'kelp': {
      // Three blades, each one closed pair of quadratic curves.
      const blades = [
        [-8, 14, -17, 2, -9, -8, -8, 3, -3, 14],
        [-3, 14, -4, -2, 0, -15, 5, 0, 3, 14],
        [3, 14, 8, 3, 10, -5, 16, 4, 8, 14],
      ];
      for (const [x0, y0, c0x, c0y, mx, my, c1x, c1y, x1, y1] of blades) {
        const pts = [x0, y0];
        quadratic(pts, x0, y0, c0x, c0y, mx, my);
        quadratic(pts, mx, my, c1x, c1y, x1, y1);
        add(pts);
      }
      break;
    }
    case 'battery': {
      // The tube on its dome. The card tilts the context, so the two ends of
      // the tube carry the tilt.
      const dome: number[] = [];
      arcInto(dome, 0, 14, 9, Math.PI, Math.PI * 2);
      add(dome);
      const [x0, y0] = rot(-10, -2, TILT), [x1, y1] = rot(12, -2, TILT);
      add(capsule(x0, y0, x1, y1, 6));
      break;
    }
    case 'throne':
      // The dais, the seat and the crown.
      add([-11, 14, 11, 14, 11, 2, -11, 2]);
      add([-6, 2, 6, 2, 6, -9, -6, -9]);
      add([-7, -9, -7, -17, -3.5, -12, 0, -20, 3.5, -12, 7, -17, 7, -9]);
      break;
    case 'drydock': {
      // The cradle, the hull in it and one chock.
      add([-15, 2, -12, 14, 12, 14, 15, 2, 11.5, 2, 9.5, 10.5,
        -9.5, 10.5, -11.5, 2]);
      const hull = [-11, 1];
      quadratic(hull, -11, 1, 0, 10, 11, 1);
      quadratic(hull, 11, 1, 0, -3, -11, 1);
      add(hull);
      add([-3, 0, 3, 0, 3, -6, -3, -6]);
      break;
    }
    case 'archive': {
      // The arched door, with the keyhole cut out of it in the water's dark.
      const door = [-10, 14, -10, -4];
      arcInto(door, 0, -4, 10, Math.PI, Math.PI * 2);
      door.push(10, 14);
      add(door);
      add(round(0, 0, 2.6), true);
      add([-1.4, 2, -2.6, 8, 2.6, 8, 1.4, 2], true);
      break;
    }
    case 'marshal':
      // The pediment, the entablature, four columns and the steps.
      add([-15, -4, 0, -13, 15, -4]);
      add([-15, -4, 15, -4, 15, -1, -15, -1]);
      for (const x of [-11, -3.7, 3.7, 11]) add(capsule(x, -1, x, 9, 3));
      add([-15, 9, 15, 9, 17, 14, -17, 14]);
      break;
    case 'ward':
      // The coral fan on its stem, with a bud on every tip.
      add(capsule(0, 14, 0, 4, 3));
      for (const [x, y] of [[-12, -5], [-5, -12], [5, -12], [12, -5]]) {
        add(capsule(0, 4, x, y, 3));
        add(round(x, y, 2.4));
      }
      add(capsule(-6, -2, -11, -12, 2));
      add(capsule(6, -2, 11, -12, 2));
      add(round(-11, -12, 1.8));
      add(round(11, -12, 1.8));
      break;
  }
  out.push({ pts: capsule(-18, 14, 18, 14, PLINTH) });
  return out;
}

/** One polygon cut to one half-plane. */
function clipHalf(pts: number[], h: Half): number[] {
  const n = pts.length / 2;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = pts[2 * i], ay = pts[2 * i + 1];
    const bx = pts[2 * j], by = pts[2 * j + 1];
    const fa = h.nx * ax + h.ny * ay + h.d;
    const fb = h.nx * bx + h.ny * by + h.d;
    if (fa >= 0) out.push(ax, ay);
    if ((fa >= 0) !== (fb >= 0)) {
      const t = fa / (fa - fb);
      out.push(ax + (bx - ax) * t, ay + (by - ay) * t);
    }
  }
  return out;
}

/** Every shape of a silhouette, filled in one ink at one alpha. */
function fillSolids(
  g: MarkGraphics, solids: readonly Solid[], color: number, a: number,
): void {
  for (const s of solids) {
    g.poly(s.pts);
    g.fill(s.water ? { color: WATER, alpha: 1 } : { color, alpha: a });
  }
}

/**
 * The piece that broke off, in the attacker's ink.
 *
 * Every shape slides by the card's offset and is then cut to the region
 * right of the crack, band by band. A shape in the water's dark stays in the
 * water's dark, so the keyhole of the Archive Vault stays cut out.
 */
function fillBroken(
  g: MarkGraphics, solids: readonly Solid[], color: number,
): void {
  for (const s of solids) {
    const moved = s.pts.map((v, i) => v + (i & 1 ? CRACK_DY : CRACK_DX));
    for (const band of CRACK_BANDS) {
      let piece = moved;
      for (const h of band) {
        piece = clipHalf(piece, h);
        if (piece.length < 6) break;
      }
      if (piece.length < 6) continue;
      g.poly(piece);
      g.fill(s.water ? { color: WATER, alpha: 1 }
        : { color, alpha: BROKEN_ALPHA });
    }
  }
}

/** One kind of the `silhouette` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  const solids = solidsOf(kind);
  return (g: MarkGraphics, ink: StructureInk): void => {
    fillSolids(g, solids, ink.own, ink.held ? 0.95 : 0.6);
    if (ink.foe === 0) return;
    fillBroken(g, solids, ink.foe);
    g.poly(CRACK_PATH, false);
    stroke(g, ink.foe, CRACK_ALPHA, 1.4);
  };
}

/** Card 05, all nine kinds. */
export const SILHOUETTE_SET: StructureSet = setOf(markOf);
